import { WidgetTypesEnum } from '@core/types'

import type { CustomEventTarget } from './customEvent'
import { customEvent } from './customEvent'
import { EventType } from './eventType'

/**
 * Public `detail.type` for `fw:widget:render-outcome` — one literal per `WidgetTypesEnum`
 * (underscores in enum values become hyphens in the payload).
 */
export type WidgetRenderOutcomeWidgetType =
  | 'embed-feed'
  | 'storyblock'
  | 'storylink'
  | 'herounit'
  | 'channel-button'
  | 'add-to-calendar'
  | 'share'
  | 'programmatic'
  | 'player'
  | 'video-chat'
  | 'unknown'

export type WidgetRenderOutcomeStatus = 'ready' | 'empty' | 'error'

export type WidgetRenderOutcomeDetail = {
  type: WidgetRenderOutcomeWidgetType
  status: WidgetRenderOutcomeStatus
  reason?: string
}

/**
 * Maps widget enum to the public `fw:widget:render-outcome` payload `type` field.
 */
export function widgetEnumToRenderOutcomeType(
  widgetType: WidgetTypesEnum | undefined,
): WidgetRenderOutcomeWidgetType | null {
  if (widgetType === undefined) {
    return null
  }
  switch (widgetType) {
    case WidgetTypesEnum.EMBED_FEED:
      return 'embed-feed'
    case WidgetTypesEnum.STORYBLOCK:
      return 'storyblock'
    case WidgetTypesEnum.STORYLINK:
      return 'storylink'
    case WidgetTypesEnum.HEROUNIT:
      return 'herounit'
    case WidgetTypesEnum.CHANNEL_BUTTON:
      return 'channel-button'
    case WidgetTypesEnum.ADD_TO_CALENDAR:
      return 'add-to-calendar'
    case WidgetTypesEnum.SHARE:
      return 'share'
    case WidgetTypesEnum.PROGRAMMATIC:
      return 'programmatic'
    case WidgetTypesEnum.PLAYER:
      return 'player'
    case WidgetTypesEnum.VIDEO_CHAT:
      return 'video-chat'
    case WidgetTypesEnum.UNKNOWN:
      return 'unknown'
    default: {
      const _missing: never = widgetType
      return _missing
    }
  }
}

/**
 * Fires once per widget init when feed / player resolution outcome is known.
 */
export function dispatchWidgetRenderOutcome(options: {
  target?: CustomEventTarget | null
  detail: WidgetRenderOutcomeDetail
}): void {
  const { type, status, reason } = options.detail
  const data: Record<string, string> = { type, status }
  if (reason !== undefined && reason !== null && reason !== '') {
    data.reason = reason
  }
  customEvent({
    type: EventType.FW_WIDGET_RENDER_OUTCOME,
    target: options.target,
    data,
  })
}
